import { Button, ConfirmSheet, Icons, StateScreen } from "@jod/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { type BlockedUser, customerBackendApi } from "../features/api/backend.ts";
import { useT } from "../features/i18n/index.ts";
import { useSession } from "../features/session/session-context.tsx";

export const Route = createFileRoute("/blocked")({
  component: BlockedListScreen,
});

/** FR-CUS-124: `/me` block-list view — see who is blocked, and unblock. */
function BlockedListScreen() {
  const { backendMode } = useSession();
  return backendMode ? <BackendBlockedList /> : <MockBlockedList />;
}

const BLOCKS_QUERY_KEY = ["customer", "blocks"] as const;

function BackendBlockedList() {
  const t = useT();
  const navigate = useNavigate();
  const { toast } = useSession();
  const queryClient = useQueryClient();
  const [confirmTarget, setConfirmTarget] = useState<BlockedUser | null>(null);

  const blocksQuery = useQuery({
    queryKey: BLOCKS_QUERY_KEY,
    queryFn: () =>
      customerBackendApi?.listBlocks() ??
      Promise.reject(new Error("Customer backend API unavailable")),
  });

  const blocks = blocksQuery.data ?? [];

  const confirmUnblock = async () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    if (!target || !customerBackendApi) return;
    try {
      await customerBackendApi.unblock(target.blockedUserId);
      toast(t("blocked.unblockedToast", { name: target.displayName }));
      await queryClient.invalidateQueries({ queryKey: BLOCKS_QUERY_KEY });
    } catch {
      toast(t("blocked.unblockFailed"));
    }
  };

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/me" })}
      >
        <Icons.IconBack size={18} /> {t("common.back")}
      </button>

      <div className="space-y-1">
        <div className="jod-kicker">{t("blocked.kicker")}</div>
        <h1 className="h-display text-[1.6rem]">{t("blocked.title")}</h1>
        <p className="text-cream-3 text-sm">{t("blocked.subtitle")}</p>
      </div>

      {blocks.length === 0 ? (
        <div className="mt-6 h-[50vh]">
          <StateScreen
            title={blocksQuery.isLoading ? t("blocked.loading") : t("blocked.emptyTitle")}
            body={t("blocked.emptyBody")}
          />
        </div>
      ) : (
        <div className="mt-5 space-y-2.5">
          {blocks.map((block) => (
            <div key={block.id} className="glass flex items-center gap-3 rounded-lg px-4 py-3.5">
              <span className="text-cream-2">
                <Icons.IconShield size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-cream text-sm">{block.displayName}</div>
                {block.reason ? (
                  <div className="truncate text-cream-3 text-xs">
                    {t("order.reason", { reason: block.reason })}
                  </div>
                ) : null}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setConfirmTarget(block)}>
                {t("blocked.unblock")}
              </Button>
            </div>
          ))}
        </div>
      )}

      <ConfirmSheet
        open={confirmTarget !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmTarget(null);
        }}
        title={t("blocked.confirmTitle", { name: confirmTarget?.displayName ?? "" })}
        description={t("blocked.confirmBody")}
        confirmLabel={t("blocked.unblock")}
        cancelLabel={t("common.notYet")}
        onConfirm={() => {
          void confirmUnblock();
        }}
      />
    </div>
  );
}

/** Mock mode: local `blockedTables` (table codes) with local unblock. */
function MockBlockedList() {
  const t = useT();
  const navigate = useNavigate();
  const { blockedTables, unblockTable, toast } = useSession();
  const [confirmTarget, setConfirmTarget] = useState<string | null>(null);

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/me" })}
      >
        <Icons.IconBack size={18} /> {t("common.back")}
      </button>

      <div className="space-y-1">
        <div className="jod-kicker">{t("blocked.kicker")}</div>
        <h1 className="h-display text-[1.6rem]">{t("blocked.title")}</h1>
        <p className="text-cream-3 text-sm">{t("blocked.subtitle")}</p>
      </div>

      {blockedTables.length === 0 ? (
        <div className="mt-6 h-[50vh]">
          <StateScreen title={t("blocked.emptyTitle")} body={t("blocked.emptyBody")} />
        </div>
      ) : (
        <div className="mt-5 space-y-2.5">
          {blockedTables.map((code) => (
            <div key={code} className="glass flex items-center gap-3 rounded-lg px-4 py-3.5">
              <span className="text-cream-2">
                <Icons.IconShield size={18} />
              </span>
              <div className="flex-1 text-cream text-sm">{t("common.table", { code })}</div>
              <Button size="sm" variant="ghost" onClick={() => setConfirmTarget(code)}>
                {t("blocked.unblock")}
              </Button>
            </div>
          ))}
        </div>
      )}

      <ConfirmSheet
        open={confirmTarget !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmTarget(null);
        }}
        title={t("blocked.confirmTableTitle", { code: confirmTarget ?? "" })}
        description={t("blocked.confirmBody")}
        confirmLabel={t("blocked.unblock")}
        cancelLabel={t("common.notYet")}
        onConfirm={() => {
          if (confirmTarget) {
            unblockTable(confirmTarget);
            toast(t("blocked.unblockedTableToast", { code: confirmTarget }));
          }
          setConfirmTarget(null);
        }}
      />
    </div>
  );
}
